export const AUTH_RETURN_PATH_STORAGE_KEY = 'deploy_console_auth_return_path';

type StorageLike = Pick<Storage, 'getItem' | 'removeItem' | 'setItem'>;

export type AuthReturnState = {
  return_path: string;
};

export function normalizeAuthReturnPath(value: unknown): string | null {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return null;
  }
  if (
    value.includes('\\') ||
    Array.from(value).some((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127;
    })
  ) {
    return null;
  }
  try {
    const parsed = new URL(value, 'https://console.invalid');
    if (parsed.origin !== 'https://console.invalid') {
      return null;
    }
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return null;
  }
}

/**
 * Return the current SPA destination when it is safe and useful to preserve
 * across OIDC. Token-bearing routes have dedicated redacted handling.
 */
export function authReturnPathFromLocation(
  pathname: string,
  search: string,
  hash: string
): string | null {
  if (
    pathname === '/logout' ||
    pathname === '/silent-renew.html' ||
    pathname === '/login/tool-token' ||
    pathname === '/invitations' ||
    pathname.startsWith('/invitations/') ||
    pathname === '/ai-fence/link/authorize'
  ) {
    return null;
  }
  return normalizeAuthReturnPath(`${pathname}${search}${hash}`);
}

export function storeAuthReturnPath(storage: StorageLike, path: string | null): void {
  const normalized = normalizeAuthReturnPath(path);
  if (normalized) {
    storage.setItem(AUTH_RETURN_PATH_STORAGE_KEY, normalized);
  }
}

export function pendingAuthReturnPath(storage: StorageLike): string | null {
  return normalizeAuthReturnPath(storage.getItem(AUTH_RETURN_PATH_STORAGE_KEY));
}

export function takeAuthReturnPath(storage: StorageLike): string | null {
  const path = pendingAuthReturnPath(storage);
  storage.removeItem(AUTH_RETURN_PATH_STORAGE_KEY);
  return path;
}

export function authReturnPathFromState(state: unknown): string | null {
  if (!state || typeof state !== 'object') {
    return null;
  }
  return normalizeAuthReturnPath((state as { return_path?: unknown }).return_path);
}

export function takeAuthCallbackReturnPath(storage: StorageLike, state: unknown): string | null {
  const statePath = authReturnPathFromState(state);
  const storedPath = takeAuthReturnPath(storage);
  return statePath ?? storedPath;
}
